/**
 * Release-aware filtering for the People Leader's Impact Portfolio surfaces.
 *
 * BINARY release model (June 2026): a team member either shares an entry or not
 * (`isReleased`). Which check-in/period it surfaces in is decided by capture
 * period (+ carry-forward), NOT by session. So the People Leader sees an entry
 * when it's released AND belongs to the period in view — or when it's a
 * measurement (always-shared KPI backing data). Every check-in in a period
 * (formal or on-demand) shows the same released set.
 */
import type { EnhancedEvidenceEntry } from '@/types/evidence'

/**
 * A measurement: a KPI-linked entry carrying a value. Measurements are the
 * backing evidence for a KPI number and are ALWAYS shared with both parties
 * (not release-gated), so the People Leader's portfolio shows them regardless
 * of the release flag — including ones the PL recorded themselves.
 */
export function isMeasurementEntry(entry: EnhancedEvidenceEntry): boolean {
  return !!entry.kpiId && (
    entry.measurementValue != null ||
    entry.midyearMeasurementValue != null ||
    entry.yearendMeasurementValue != null
  )
}

/** Whether an entry belongs to the given period (capture period, dual-period aware). */
export function matchesPeriod(entry: EnhancedEvidenceEntry, period: 'MIDYEAR' | 'YEAREND'): boolean {
  return !entry.reviewPeriod || entry.reviewPeriod === period || entry.appliesToBothPeriods === true
}

/**
 * What the People Leader sees for a period (formal overview OR any check-in in
 * that period): entries the team member SHARED that belong to the period, plus
 * every measurement for the period (always-shared). Binary — no per-session
 * distinction, so on-demand check-ins show the same set as the formal review.
 */
export function visibleForPeriod(entry: EnhancedEvidenceEntry, period: 'MIDYEAR' | 'YEAREND'): boolean {
  return (entry.isReleased === true || isMeasurementEntry(entry)) && matchesPeriod(entry, period)
}

// Back-compat aliases — every check-in in a period now shows the same released
// set, so the old per-session / formal-period helpers collapse to visibleForPeriod.
export function visibleForFormalPeriod(entry: EnhancedEvidenceEntry, period: 'MIDYEAR' | 'YEAREND'): boolean {
  return visibleForPeriod(entry, period)
}
export function visibleForSession(
  entry: EnhancedEvidenceEntry,
  _sessionId: string | null | undefined,
  period: 'MIDYEAR' | 'YEAREND',
): boolean {
  return visibleForPeriod(entry, period)
}

const carryEligibleFromEnd = (end?: string | null) => !end || new Date(end).getMonth() >= 6

/**
 * Build the Mid-Year measurements that should appear in a Year-End view as
 * read-only "carried forward" context — for carry-eligible KPIs (objective
 * extends past June, or no end_date) that have NO Year-End measurement yet.
 *
 * Eligibility is derived from the KPAs' objectives (end_date) — authoritative —
 * plus any underlying KPI explicitly tagged carryForwardEligible. Returns []
 * unless `period` is YEAREND. Each returned entry is tagged carriedFromMidYear.
 *
 * Shared by every surface (team-member journey, member-details, review
 * workspace) so the carried-forward set is identical everywhere.
 */
export function buildCarriedMidYearEvidence(
  evidence: EnhancedEvidenceEntry[],
  kpas: any[],
  period: 'MIDYEAR' | 'YEAREND',
): EnhancedEvidenceEntry[] {
  if (period !== 'YEAREND' || !Array.isArray(evidence)) return []
  const eligible = new Set<string>()
  for (const kpa of kpas || []) {
    for (const obj of kpa?.individualObjectives || []) {
      if (carryEligibleFromEnd(obj?.end_date)) {
        for (const k of obj?.kpis || []) if (k?.id) eligible.add(String(k.id))
      }
    }
    for (const k of kpa?.underlyingKPIs || []) {
      if (k?.id && k.carryForwardEligible === true) eligible.add(String(k.id))
    }
  }
  const yeKpiIds = new Set(
    evidence
      .filter(e => e.reviewPeriod === 'YEAREND' && isMeasurementEntry(e) && e.kpiId)
      .map(e => String(e.kpiId)),
  )
  return evidence
    .filter(e => e.reviewPeriod === 'MIDYEAR' && isMeasurementEntry(e) && e.kpiId
      && eligible.has(String(e.kpiId)) && !yeKpiIds.has(String(e.kpiId)))
    .map(e => ({ ...e, carriedFromMidYear: true }))
}
